import { HttpClient } from '@angular/common/http'
import { Injectable, OnDestroy } from '@angular/core'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { NotificationChannel } from '@app/modules/database/models/resend-confirmation-pricing'
import { IRunningJob } from '@app/modules/shared/header/running-jobs/model/running-jobs.interface'
import { environment } from '@environments/environment'
import { Observable, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { CustomInviteButtonConfig, InvitationInfo, InviteContentPayload, InviteTestPayload } from '../models/invitation-info.interface'

/**
 * Service used to invite registrants to a different event under the same workspace
 */
@Injectable({
  providedIn: 'root'
})
export class InviteService implements OnDestroy {
  /** Default confirm/decline button config for custom template if user did not customize. */
  private readonly _defaultCustomButtons: CustomInviteButtonConfig = {
    confirm: { text: 'Confirm', textColor: '#ffffff', backgroundColor: '#22c55e', isBold: false, isItalic: false, isUnderline: false, fontSize: '16px' },
    decline: { text: 'Decline', textColor: '#ffffff', backgroundColor: '#ef4444', isBold: false, isItalic: false, isUnderline: false, fontSize: '16px', hidden: false }
  }
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /**
   * Creates the service and injects it's dependencies
   * @param http HttpClient
   * @param auth AuthService
   */
  constructor(private http: HttpClient, private auth: AuthService) {}
  /**
   * Invites registrants to a new event that belongs to the current workspace
   * @param info Invitation information
   * @returns Observable
   * @deprecated Replaced by invitation-collection/add — kept commented for reference.
   */
  // invite(info: InvitationInfo) {
  //   const payload: InvitePayload = {
  //     action: info.action,
  //     ids: info.ids,
  //     invite_message: info.message,
  //     invite_event: info.eventSlug,
  //     invite_form: info.formSlug,
  //     emails: info.emails,
  //     email_template: info.selectedTemplate === 0 ? 4 : info.selectedTemplate,
  //     count: info.selectionCount,
  //     send_method: info.inviteChannel ?? NotificationChannel.EMAIL,
  //     parent_id: info.action === 'exclude' ? info.parent_id : undefined,
  //     search: info.search
  //   }
  //
  //   this._applyCustomTemplateFieldsFromInfo(payload, info)
  //
  //   payload.confirm_font_size = this._normalizeFontSizeValue(payload.confirm_font_size)
  //   payload.decline_font_size = this._normalizeFontSizeValue(payload.decline_font_size)
  //
  //   return this.http
  //     .patch<{ batch_id: string; status: string; total: number; message?: string }>(
  //       `${environment.registrationsApi}invite-bulk/${this.auth.currentWorkspace.slug}/${info.fromEventSlug}/${info.fromFormSlug}`,
  //       payload
  //     )
  //     .pipe(takeUntil(this._destroy$))
  // }

  /**
   * Sends a single test invite (same template/message fields as bulk, plus receiver).
   */
  inviteTest(info: InvitationInfo, receiver: string) {
    const body: InviteTestPayload = {
      receiver: receiver.trim(),
      send_method: info.inviteChannel ?? NotificationChannel.EMAIL,
      invite_message: info.message,
      invite_event: info.eventSlug,
      invite_form: info.formSlug,
      email_template: info.selectedTemplate === 0 ? 4 : info.selectedTemplate,
      subject: info.subject
    }
    this._applyCustomTemplateFieldsFromInfo(body, info)
    // Final payload safety net: always send button font sizes as "Npx"
    body.confirm_font_size = this._fontSizeCssValue(body.confirm_font_size)
    body.decline_font_size = this._fontSizeCssValue(body.decline_font_size)

    const url = `${environment.registrationsApi}invite-test/${this.auth.currentWorkspace.slug}/${info.fromEventSlug}/${info.fromFormSlug}`
    return this.http.patch(url, body).pipe(takeUntil(this._destroy$))
  }

  /** Custom template (template id 0) — shared by bulk invite and invite-test. */
  private _applyCustomTemplateFieldsFromInfo(
    target: Pick<
      InviteContentPayload,
      | 'confirm_background_color'
      | 'confirm_text_color'
      | 'confirm_invitation_text'
      | 'confirm_is_italic'
      | 'confirm_is_bold'
      | 'confirm_is_underline'
      | 'confirm_font_size'
      | 'decline_background_color'
      | 'decline_text_color'
      | 'decline_invitation_text'
      | 'decline_is_italic'
      | 'decline_is_bold'
      | 'decline_is_underline'
      | 'decline_font_size'
      | 'image_url'
      | 'card_background'
      | 'calender_button'
      | 'font_family'
    >,
    info: InvitationInfo
  ) {
    if (info.selectedTemplate !== 0) return
    const buttons = info.customButtons ?? this._defaultCustomButtons
    target.font_family = info.fontFamily
    target.confirm_background_color = buttons.confirm.backgroundColor
    target.confirm_text_color = buttons.confirm.textColor
    target.confirm_invitation_text = buttons.confirm.text
    target.confirm_is_italic = !!buttons.confirm.isItalic
    target.confirm_is_bold = !!buttons.confirm.isBold
    target.confirm_is_underline = !!buttons.confirm.isUnderline
    target.confirm_font_size = this._fontSizeCssValue(buttons.confirm.fontSize)
    target.decline_background_color = buttons.decline.backgroundColor
    target.decline_text_color = buttons.decline.textColor
    target.decline_invitation_text = buttons.decline.hidden ? '' : buttons.decline.text
    target.decline_is_italic = !!buttons.decline.isItalic
    target.decline_is_bold = !!buttons.decline.isBold
    target.decline_is_underline = !!buttons.decline.isUnderline
    target.decline_font_size = this._fontSizeCssValue(buttons.decline.fontSize)
    target.image_url = info.imageUrl
    target.card_background = info.cardBackgroundColor
    target.calender_button = info.calendarButtonColor
  }

  /**
   * Ensures font size values are sent as "Npx" for API payload consistency.
   */
  private _fontSizeCssValue(fontSize: string | number | undefined): string {
    if (fontSize === undefined || fontSize === null || fontSize === '') return '16px'
    if (typeof fontSize === 'number') return `${fontSize}px`
    const value = String(fontSize).trim()
    const numericPart = value.replace(/px$/i, '').trim()
    return /^\d+$/.test(numericPart) ? `${numericPart}px` : '16px'
  }

  /**
   * Get the count of the unsettled registrants
   * @param info Invitation information
   */
  unsSettledCount(info: Pick<InvitationInfo, 'action' | 'ids' | 'eventSlug' | 'formSlug' | 'selectionCount'>): Observable<any> {
    return this.http.post(`${environment.registrationsApi}count/unsettled/${this.auth.currentWorkspace.slug}/${info.eventSlug}/${info.formSlug}`, {
      action: info.action,
      ids: info.ids,
      count: info.selectionCount
    })
  }

  getRunningJobs(workspace_slug: string): Observable<IRunningJob[]> {
    return this.http.get<IRunningJob[]>(`${environment.planningDashboard}statistics/${workspace_slug}`)
  }
}
